import React from "react";
import { useGetLegalDates } from "../../api/queries";

interface TermsContentProps {
  showLastUpdated?: boolean;
}

const TermsContent: React.FC<TermsContentProps> = ({
  showLastUpdated = true,
}) => {
  const { data: legalDates } = useGetLegalDates();

  const termsLastUpdated = legalDates?.termsLastUpdated
    ? new Date(legalDates.termsLastUpdated).toLocaleDateString()
    : "Loading...";

  return (
    <>
      {showLastUpdated && (
        <p className="text-sm text-gray-600 mb-6">
          Last updated: {termsLastUpdated}
        </p>
      )}

      <div className="prose prose-sm max-w-none text-gray-700 space-y-4">
        <p className="text-sm italic">
          These Terms &amp; Conditions (&quot;Terms&quot;) apply to all PSYCH
          FOR LIFE<sup>®</sup> websites and applications of The Arizona Board
          of Regents for and on behalf of Arizona State University
          (&quot;Arizona State University,&quot; &quot;we&quot; or
          &quot;us&quot;) (collectively, the &quot;Services&quot;).
        </p>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            1. Agreement to Terms
          </h2>
          <p>
            Please read these Terms carefully. By accessing or using the
            Services, you acknowledge that you have read these Terms, that these
            Terms govern your use of the Services, and that you agree to them.
            If you do not agree to be bound by these Terms, please do not use
            the Services.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            2. Additional Terms
          </h2>
          <p>
            For those of you who are accessing PSYCH FOR LIFE<sup>®</sup>{" "}
            through your employer or another organization, these Terms are in
            addition to and subject to any applicable agreement between Arizona
            State University and your employer, including a Master Service
            Agreement (&quot;MSA&quot;) or Beta Testing Agreement. Unless we say
            otherwise in the other agreement, in the event of any inconsistency
            between these Terms and your other agreement, your other agreement
            will control. In order to access some of the Services you may be
            required to agree to additional terms and conditions which
            supplement these Terms. An example of additional terms and
            conditions that apply to some aspect of the Services includes but is
            not limited to the End User License Agreement (&quot;EULA&quot;) for
            PSYCH FOR LIFE<sup>®</sup> mobile applications. Where such
            additional terms and conditions are inconsistent with these Terms
            and we do not indicate otherwise, the additional terms will control.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            3. Service Use
          </h2>
          <p>
            By registering for an account and using and/or accessing the
            Services, you warrant that you are of legal age to use the Services
            and reside in the United States. To use the Services, you must
            register for an account. You must provide accurate account
            information and keep this information updated. You are responsible
            for maintaining the confidentiality of your account and password and
            for restricting access. For the avoidance of doubt, you are
            responsible for all activities that occur in connection with your
            account to the extent permitted by applicable law. You must notify
            us promptly at{" "}
            <a
              href="mailto:support@psychforlife.org"
              className="text-blue-500 hover:text-blue-600 underline"
            >
              support@psychforlife.org
            </a>{" "}
            if you become aware of any unauthorized access or use of your
            account.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            4. Changes to Terms
          </h2>
          <p>
            Arizona State University reserves the right to change or update
            these Terms from time to time in our sole discretion, and we will
            inform you about all updates when they occur. All changes are
            effective immediately when we post them and apply to all access and
            use of the Services thereafter. Disputes arising under these Terms
            will be resolved in accordance with the version of these Terms in
            place at the time the dispute arose. Your use of the Services
            following the effective date of any changes to these Terms will
            constitute your acceptance of such changes. If you do not agree to
            the updated Terms, you should terminate your account and stop using
            the Services.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            5. Nature of Subscription
          </h2>
          <p>
            You may access the Services pursuant to these Terms and Conditions,
            or pursuant to an applicable agreement between Arizona State
            University and your employer. Once your eligibility through such
            agreement expires, your right to access the Services pursuant to
            such agreement will automatically terminate.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            6. Termination
          </h2>
          <p>
            Arizona State University may suspend or terminate your use of the
            Services without notice to you if Arizona State University in its
            sole discretion believes you violated your agreement and/or these
            Terms. If you access the Services through your employer or other
            organization, your access to the Services may also be terminated if
            the relationship between Arizona State University and your employer
            or organization terminates or expires, or if your relationship with
            your employer or organization ends. You may cancel your account at
            any time by sending an email to us at{" "}
            <a
              href="mailto:support@psychforlife.org"
              className="text-blue-500 hover:text-blue-600 underline"
            >
              support@psychforlife.org
            </a>
            . Upon any termination, discontinuation, or cancellation of the
            Services or your account, all provisions of these Terms which by
            their nature survive will survive, including, without limitation,
            ownership provisions, warranty disclaimers, limitations of
            liability, governing law, and dispute resolution provisions.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            7. Provision of Access to Services
          </h2>
          <p>
            Subject to and conditioned on compliance with these Terms and any
            Additional Terms, you may access and use the Services on a
            non-exclusive and non-transferable basis during the term of your
            agreement in the United States solely for your personal
            non-commercial purposes.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            8. Prohibited Use
          </h2>
          <p>You shall not at any time:</p>
          <ol className="list-[lower-alpha] pl-6 space-y-2">
            <li>
              Create, submit, transmit, or share any unlawful User Content or
              any other content to or through the Services, including threats of
              violence, obscenity, child pornography, or harassing content;
            </li>
            <li>
              Copy, reproduce, publicly perform, publicly display, modify,
              create derivative works of, mirror or frame the Services, or any
              individual element within the Services, without express written
              consent from Arizona State University;
            </li>
            <li>
              Create links to, interfaces, or otherwise connect any third-party
              to the Services, or develop or use any applications that interact
              with the Services without prior written consent of Arizona State
              University;
            </li>
            <li>
              Remove, delete, alter, or obscure any copyright, trademark,
              patent, or other intellectual property or proprietary rights
              notices from the Services;
            </li>
            <li>
              Redistribute, sublicense, sell or otherwise make the Services
              available to third parties;
            </li>
            <li>
              Reverse engineer, disassemble, decompile, decode, adapt, or
              otherwise attempt to derive or gain access to the source code of
              any of the Software (as contained in the Services) or any part
              thereof or the underlying ideas or algorithms thereof by any means
              whatsoever;
            </li>
            <li>
              Input, upload, transmit or otherwise provide to or through the
              Services any information or materials that are unlawful or
              injurious, or contain, transmit, or activate any viruses,
              corrupted data, or other harmful, disruptive, or destructive files
              or content;
            </li>
            <li>
              Avoid, bypass, ignore, remove, deactivate, impair, descramble, or
              otherwise circumvent any technological measure protecting the
              Services;
            </li>
            <li>
              Use the Services other than for their intended purposes and in any
              manner that is not permitted by your underlying agreement and/or
              these Terms, including for any manner or for any purpose that
              infringes, misappropriates, or otherwise violates any intellectual
              property right or other right of any person, or that violates any
              applicable law.
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            9. Ownership
          </h2>
          <p>
            Arizona State University and its licensors exclusively own all
            right, title, and interest in and to the Services, including all
            text, graphics, images, audio, video, code, or other materials made
            available via the Services, and all associated intellectual property
            rights. You acknowledge that the Services are protected by
            intellectual property rights and other laws of the U.S. and foreign
            countries. You may not remove, alter, or obscure any copyright,
            trademark, service mark, or other proprietary rights notices
            incorporated in or accompanying any part of the Services. You may
            not use the Services except as provided for in these Terms, any
            other agreement, and any other additional terms and conditions. If
            we provide mobile applications for download, you may download a copy
            of such mobile application to your device solely for your own
            personal, non-commercial use subject to our EULA. Subject to such
            limited license granted under the EULA, you may not otherwise
            download, copy, or reproduce any of the Software as contained in the
            Services. Audio or video content not explicitly indicated as
            downloadable may not be downloaded or copied from the Services. You
            may not distribute, modify, transfer, create derivative works from,
            or sell the Services. If you make other use of the Services except
            as provided above, you may violate copyright and other laws of the
            United States, and other countries.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            10. Trademarks
          </h2>
          <p>
            You must not use any names, service marks, trademarks, trade names,
            logos or other identifying names, domain names, or identifying marks
            of Arizona State University, whether that be ARIZONA STATE
            UNIVERSITY<sup>®</sup>, PSYCH FOR LIFE<sup>®</sup> or otherwise,
            without our prior written permission. The absence of a trademark
            from this list does not constitute a waiver of Arizona State
            University&apos;s trademark or other intellectual property rights
            concerning that trademark. All third-party trademarks mentioned in
            the Services are the property of their respective owners. Reference
            to any third-party products, services, or other information by trade
            name, trademark, or otherwise does not constitute or imply
            endorsement, sponsorship, or recommendation by Arizona State
            University.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            11. Upgrades and Modification to Services
          </h2>
          <p>
            The Services may be updated and modified from time to time in
            Arizona State University&apos;s sole and reasonable discretion. You
            agree that we have no obligation to provide any Upgrades or to
            continue to provide or enable any particular features or
            functionality. We are not responsible for any loss or harm related
            to your inability to access or use the Services. Access to the
            Services may be occasionally restricted to allow for repairs or
            maintenance. We may change or discontinue, temporarily, or
            permanently, any feature, component, or content of the Services at
            any time without notice. You acknowledge and agree Arizona State
            University is not liable for any unavailability, modification,
            suspension, or discontinuance of any feature of or component of the
            Services.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            12. Feedback
          </h2>
          <p>
            If you send or transmit any communications or materials to us
            suggesting or recommending changes to the Services, including
            without limitation, new features or functionality relating thereto,
            or any comments, questions, suggestions, or the like
            (&quot;Feedback&quot;), Arizona State University is free to use such
            Feedback. You hereby assign to Arizona State University, all right,
            title, and interest in, and we are free to use, without any
            attribution or compensation to any party, any ideas, know-how,
            concepts, techniques, or other intellectual property rights
            contained in the Feedback, for any purpose whatsoever, although we
            are not required to use any Feedback.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            13. Third-Party Content
          </h2>
          <p>
            The Services may contain information about, and links to,
            third-party products, services, websites, resources, activities, or
            events, and we may allow third-parties to make their content and
            information available on or through the Services (collectively,
            &quot;Third-Party Content&quot;). We provide Third-Party Content
            only as a convenience and do not control or endorse, and make no
            representations or warranties regarding, any Third-Party Content. To
            the extent permitted by applicable laws, you acknowledge sole
            responsibility for, and assume all risk arising from, your access to
            and use of such Third-Party Content.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            14. Copyright Complaints
          </h2>
          <p>
            If you believe that anything on the Services infringes any copyright
            you own or control, you may notify Arizona State University&apos;s
            designated agent as follows:
          </p>
          <ol className="list-[lower-alpha] pl-6 space-y-2">
            <li>Designated Agent: ET Cybersecurity</li>
            <li>
              Address: Arizona State University
              <br />
              Enterprise Technology
              <br />
              1551 S. Rural Rd.
              <br />
              Tempe, AZ 85287
            </li>
            <li>
              Email:{" "}
              <a
                href="mailto:copyright.infringement@asu.edu"
                className="text-blue-500 hover:text-blue-600 underline"
              >
                copyright.infringement@asu.edu
              </a>
            </li>
            <li>
              Please see our Copyright Policy for instructions on sending us a
              notice of copyright infringement.
            </li>
            <li>
              It is the policy of Arizona State University to terminate accounts
              of repeat infringers.
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            15. Privacy Policy
          </h2>
          <p>
            You may be required to provide certain information about yourself as
            a condition to using the Services or certain of its features. All
            information we collect through or in connection with the Services
            (including, without limitation, de-identified and aggregated data)
            is further described in and subject to our Privacy Notice and
            applicable law. By using the Services, you consent to all actions
            taken by us with respect to your information in compliance with the
            Privacy Notice.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            16. Medical Disclaimer
          </h2>
          <p>
            THE SERVICES ARE PROVIDED FOR INFORMATIONAL PURPOSES ONLY AND ARE
            NOT INTENDED, DESIGNED, OR IMPLIED TO DIAGNOSE, PREVENT, OR TREAT
            ANY CONDITION OR DISEASE, WHETHER MENTAL HEALTH OR OTHERWISE, OR TO
            BE A SUBSTITUTE FOR PROFESSIONAL MEDICAL OR MENTAL HEALTH CARE.
            PROVIDER IS NOT YOUR HEALTHCARE PROVIDER. PROVIDER IS NOT AND DOES
            NOT PROVIDE 911 OR OTHER EMERGENCY SERVICES AND MAY NOT CONTACT YOU
            OR ANYONE ON YOUR BEHALF WITH RESPECT TO YOUR MEDICAL CONDITION OR
            TREATMENT. IF YOU ARE EXPERIENCING A MENTAL HEALTH OR OTHER MEDICAL
            EMERGENCY, PLEASE CONTACT 911 OR GO TO THE NEAREST EMERGENCY ROOM.
            YOU SHOULD ALWAYS CONSULT A MEDICAL PROFESSIONAL IF YOU HAVE ANY
            QUESTIONS REGARDING A MEDICAL CONDITION. YOU SHOULD NEVER DISREGARD
            PROFESSIONAL MEDICAL ADVICE OR DELAY IN SEEKING IT BECAUSE OF
            SOMETHING YOU HAVE READ OR RECEIVED USING THE SERVICES. PROVIDER IS
            NOT RESPONSIBLE FOR THE ACCURACY, RELIABILITY, EFFECTIVENESS, OR
            CORRECT USE OF INFORMATION YOU RECEIVE THROUGH OR AS GENERATED BY
            THE SERVICES.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            17. Warranty Disclaimer
          </h2>
          <p>
            THE SERVICES ARE PROVIDED &quot;AS IS&quot; AND PROVIDER HEREBY
            DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR
            OTHERWISE. PROVIDER SPECIFICALLY DISCLAIMS ALL IMPLIED WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
            NON-INFRINGEMENT, AND ALL WARRANTIES ARISING FROM COURSE OF DEALING,
            USAGE, OR TRADE PRACTICE.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            18. Sovereign Immunity and Limitation of Liability
          </h2>
          <p>
            Arizona State University expressly retains all rights and benefits
            of sovereign immunity and nothing in these Terms shall be deemed as
            a waiver of sovereign immunity. Nothing in these Terms shall inure
            to the benefit of any third party for the purpose of allowing any
            claim against Arizona State University which would otherwise be
            barred under the doctrine of sovereign immunity or operation of law.
            Without derogating from the generality of the above:
          </p>
          <p>
            IN NO EVENT WILL ARIZONA STATE UNIVERSITY BE LIABLE TO YOU FOR ANY
            USE, INTERRUPTION, DELAY, OR INABILITY TO USE THE SERVICES. YOUR
            ACCESS TO THE SERVICES IS PURSUANT TO YOUR MSA BETWEEN PROVIDER AND
            YOUR EMPLOYER AS CUSTOMER, OR PURSUANT TO ANOTHER FORM OF AGREEMENT.
            YOU ACKNOWLEDGE THAT YOU HAVE NO RIGHTS TO ENFORCE ANY OF THE TERMS
            OF YOUR MSA OR OTHER AGREEMENT. ANY OBLIGATION OR LIABILITY PROVIDER
            MAY HAVE WITH RESPECT TO YOUR USE OR INABILITY TO USE THE SERVICES
            SHALL BE SOLELY TO YOUR EMPLOYER AS CUSTOMER PURSUANT TO SUCH
            AGREEMENT AND SUBJECT TO ALL LIMITATIONS OF LIABILITY SET FORTH
            THEREIN.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            19. Notices
          </h2>
          <p>
            All notices, requests, demands and other communications hereunder
            will be deemed to have been duly given and effective upon sending if
            the same shall be in writing and be delivered by email and addressed
            to the recipient&apos;s email address. All notices given by you to
            us must be given to Arizona State University at{" "}
            <a
              href="mailto:support@psychforlife.org"
              className="text-blue-500 hover:text-blue-600 underline"
            >
              support@psychforlife.org
            </a>
            . We may give notice to you at the email address you provide to use
            when you register.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            20. Force Majeure
          </h2>
          <p>
            We will not be liable for failure to perform any obligation under
            these Terms if such failure is directly caused by a Force Majeure
            Event. A &quot;Force Majeure Event&quot; shall mean an event or
            circumstance that is beyond the reasonable control and without fault
            or negligence, and that could not have been prevented by the
            reasonable diligence. Without in any way limiting the foregoing, a
            Force Majeure Event may include, but is not restricted to, acts of
            God or of a public enemy, acts of the Government in either its
            sovereign or contractual capacity, war, riots, fires, floods,
            epidemics or pandemics, mass health issues or disease, quarantine
            restrictions, strikes or labor difficulties, civil tumult, freight
            embargoes, natural disasters, unusually severe weather, a failure or
            disruption of utilities or critical electronic systems, acts of
            terrorism, mass shootings or other emergencies that may disrupt our
            operations.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            21. Governing Law
          </h2>
          <p>
            Any dispute regarding or arising under these Terms, or the
            interpretation of these Terms, will be subject to and resolved in
            accordance with the laws of the State of Arizona, without regard to
            its conflicts of laws principles. Any legal suit, action, or
            proceeding arising out of, or related to, these Terms shall be
            instituted exclusively in the Superior Court of Arizona in Maricopa
            County, and any appellate court thereof. You waive any and all
            objections to the exercise of jurisdiction over you by such courts
            and to venue in such courts.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            22. Dispute Resolution
          </h2>
          <p>
            In the event of any dispute, claim, question, or disagreement
            arising from or relating to these Terms or the breach, termination
            or validity thereof, the Parties will first attempt to resolve the
            matter over a period of at least thirty (30) calendar days before
            resorting to formal dispute resolution, except that equitable
            remedies may be sought immediately. To this effect, they will
            consult and negotiate with each other in good faith and, recognizing
            their mutual interests, attempt to reach a just and equitable
            solution satisfactory to both parties. The parties agree to
            arbitrate disputes filed in Arizona Superior Court that are subject
            to mandatory arbitration pursuant to Arizona Revised Statutes A.R.S.
            § 12-133. A.R.S. § 12-1518 requires this provision in all Arizona
            State University contracts.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            23. Assignment
          </h2>
          <p>
            You may not assign or transfer any of your rights or obligations
            under these Terms without prior written approval. Any attempted
            assignment in violation of this provision will be null and void.
            Subject to the foregoing, these Terms will be binding upon your
            permitted successors and permitted assigns or other permitted
            transferees.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            24. Severability
          </h2>
          <p>
            If any provision of these Terms shall for any reason be found
            invalid, illegal, unenforceable, or in conflict with any valid
            controlling law: (a) such provision will be separated from these
            Terms; (b) such invalidity, illegality, unenforceability, or
            conflict will not affect any other provision hereof; and (c) these
            Terms will be interpreted and construed as if such provision, to the
            extent the same shall have been held invalid, illegal,
            unenforceable, or in conflict, had never been contained herein.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            25. Waiver
          </h2>
          <p>
            If we delay in exercising or fail to exercise any right available to
            us under these Terms, such delay or failure does not constitute a
            waiver of that right or any other rights under these Terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            26. Merger and Integration
          </h2>
          <p>
            The Terms, any additional agreement with Arizona State University,
            and any additional terms or policies referred to in these Terms
            contains the entire agreement between you and Arizona State
            University and supersedes any and all prior understandings,
            agreements, representations, and warranties, express or implied,
            written or oral, related to the Services.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-6 mb-3 text-gray-900">
            27. Failure of Legislature to Appropriate
          </h2>
          <p>
            In accordance with A.R.S. § 35-154, if Arizona State
            University&apos;s performance under these Terms depends on the
            appropriation of funds by the Arizona Legislature, and if the
            Legislature fails to appropriate the funds necessary for
            performance, then Arizona State University may provide written
            notice of this to you and cancel the Services and your account
            without further obligation of Arizona State University.
            Appropriation is a legislative act and is beyond the control of
            Arizona State University.
          </p>
        </section>
      </div>
    </>
  );
};

export default TermsContent;
